import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, SectionCard, Mono, InfoNote, copyText } from "@/components/uikit";
import { canonicalize, sha256Hex } from "@/lib/crypto";
import { HASH_MODES } from "@/lib/constants";
import { publicIdentity } from "@/lib/signing";
import { IdentityQR } from "@/components/IdentityQR";
import { downloadJson } from "@/lib/download";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

export default function KCrypto() {
  const { records, identity, createIdentity, importIdentity, clearIdentity } = useApp();
  const [input, setInput] = useState('{ "message": "SignalLink", "value": 1 }');
  const [mode, setMode] = useState("canonical");
  const [canon, setCanon] = useState("");
  const [hash, setHash] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      let c;
      if (mode === "raw") {
        c = input; // byte/text-sensitive
      } else {
        try {
          c = canonicalize(JSON.parse(input));
        } catch {
          c = canonicalize(input);
        }
      }
      const h = await sha256Hex(c);
      if (active) {
        setCanon(c);
        setHash(h);
      }
    })();
    return () => {
      active = false;
    };
  }, [input, mode]);

  const genIdentity = async () => {
    setBusy(true);
    try {
      const id = await createIdentity("SignalLink Operator");
      toast.success(`Signing identity created (${id.algorithm})`, { description: `Key ID ${id.public_key_id}` });
    } finally {
      setBusy(false);
    }
  };
  const rotate = async () => {
    setBusy(true);
    try {
      const id = await createIdentity("SignalLink Operator");
      toast.success("Identity rotated", { description: `New key ID ${id.public_key_id}` });
    } finally {
      setBusy(false);
    }
  };
  const exportPublic = () => {
    if (!identity) return;
    downloadJson(publicIdentity(identity), `signallink-public-identity-${identity.public_key_id}.json`);
    toast.success("Public identity exported");
  };
  const importFromFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      let obj;
      try {
        obj = JSON.parse(String(reader.result || ""));
      } catch {
        toast.error("Invalid JSON identity file");
        return;
      }
      const ok = await importIdentity(obj);
      if (ok) toast.success("Signing identity imported", { description: "Private key stored non-extractably (IndexedDB)." });
      else toast.error("Invalid identity file", { description: "Must be a full identity containing algorithm, public_jwk and private_jwk." });
    };
    reader.onerror = () => toast.error("Could not read file");
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <div>
      <PageHeader overline="Cryptographic Evidence Engine" title="K-Crypto · SHA-256" icon="KeyRound"
        subtitle="Native WebCrypto SHA-256 with selectable hash mode and optional local signing. No paid service or API key required." />

      <InfoNote tone="info" className="mb-6">
        K-Crypto uses the browser's built-in <Mono>crypto.subtle</Mono> engine. Records are chained by previous-record
        hash. Local hashing / signing is <strong>not</strong> blockchain, RFC 3161, government, or third-party notarization.
      </InfoNote>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title="Live Hasher" icon="Hash" testId="kcrypto-hasher-card"
          right={
            <div className="flex gap-1" role="group" aria-label="Hash mode">
              {HASH_MODES.map((m) => (
                <button key={m.value} onClick={() => setMode(m.value)} data-testid={`kcrypto-mode-${m.value}`}
                  className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                    mode === m.value ? "border-cyan-500 bg-slate-900 text-cyan-300" : "border-slate-700 text-slate-400 hover:text-slate-200"
                  }`}>
                  {m.label}
                </button>
              ))}
            </div>
          }>
          <div className="space-y-3">
            <InfoNote tone={mode === "raw" ? "warn" : "info"} className="text-[11px]">
              <strong>{mode === "raw" ? "RAW TEXT" : "CANONICAL JSON"} mode.</strong>{" "}
              {HASH_MODES.find((m) => m.value === mode)?.detail} Verification always uses the mode recorded on the receipt.
            </InfoNote>
            <Textarea data-testid="kcrypto-input" rows={6} value={input} onChange={(e) => setInput(e.target.value)}
              className="border-slate-700 bg-slate-950 font-mono text-xs text-cyan-200" />
            <div>
              <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{mode === "raw" ? "Raw input (byte-sensitive)" : "Canonical form (sorted keys)"}</p>
              <pre className="max-h-28 overflow-auto rounded border border-slate-800 bg-slate-950 p-2 font-mono text-[11px] text-slate-300" data-testid="kcrypto-canonical">{canon}</pre>
            </div>
            <div className="rounded border border-cyan-900 bg-slate-950 p-3">
              <div className="mb-1 flex items-center justify-between">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">SHA-256 digest · {mode === "raw" ? "RAW TEXT" : "CANONICAL JSON"}</p>
                <button type="button" className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                  onClick={() => copyText(hash, "Hash copied")} data-testid="kcrypto-copy-button">
                  <Icons.Copy className="h-3.5 w-3.5" /> Copy
                </button>
              </div>
              <p className="break-all font-mono text-sm text-cyan-300" data-testid="kcrypto-hash-output">{hash}</p>
              <div className="mt-2 flex items-center gap-2">
                <div className="h-1.5 flex-1 overflow-hidden rounded bg-slate-800">
                  <div className="h-full bg-cyan-500" style={{ width: "100%" }} aria-hidden="true" />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-400">256-bit integrity</span>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Signing Identity" icon="Fingerprint" testId="kcrypto-identity-card">
          {identity ? (
            <div className="space-y-3" data-testid="kcrypto-identity-active">
              <div className="rounded border border-emerald-800 bg-emerald-950/30 p-3">
                <p className="flex items-center gap-2 font-heading text-sm font-bold uppercase text-emerald-300">
                  <Icons.BadgeCheck className="h-4 w-4" /> Identity Active
                </p>
                <p className="mt-1 font-mono text-[11px] text-slate-300">algorithm: {identity.algorithm}</p>
                <p className="font-mono text-[11px] text-slate-300">key_id: <Mono>{identity.public_key_id}</Mono></p>
                <p className="font-mono text-[11px] text-slate-500">created: {new Date(identity.created_at).toLocaleString()}</p>
              </div>
              <IdentityQR signer={publicIdentity(identity)} testId="kcrypto-identity-qr" />
              <InfoNote tone="ok">The private key is stored <strong>non-extractably</strong> in the browser's IndexedDB key store — it cannot be read out, exported, logged, or transmitted. Only the public identity can be exported.</InfoNote>
              <div className="flex flex-wrap gap-2">
                <Button onClick={rotate} disabled={busy} variant="outline" className="border-amber-800 text-amber-300 hover:bg-amber-950/40" data-testid="kcrypto-rotate-button">
                  <Icons.RefreshCw className="mr-2 h-4 w-4" /> Rotate
                </Button>
                <Button onClick={exportPublic} variant="outline" className="border-slate-700 text-slate-200" data-testid="kcrypto-export-public-button">
                  <Icons.Download className="mr-2 h-4 w-4" /> Export public
                </Button>
                <Button onClick={async () => { await clearIdentity(); toast.success("Identity cleared"); }} variant="outline" className="border-rose-800 text-rose-300 hover:bg-rose-950/40" data-testid="kcrypto-clear-identity-button">
                  <Icons.Trash2 className="mr-2 h-4 w-4" /> Clear
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="kcrypto-identity-none">
              <InfoNote tone="info">
                No signing identity yet. Signing is <strong>optional</strong> — the console works fully without it.
                Generate a local Ed25519 / ECDSA key pair to sign receipts.
              </InfoNote>
              <div className="flex flex-wrap gap-2">
                <Button onClick={genIdentity} disabled={busy} className="bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400" data-testid="kcrypto-generate-identity-button">
                  {busy ? <Icons.Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icons.KeyRound className="mr-2 h-4 w-4" />} Generate Identity
                </Button>
                <Button asChild variant="outline" className="border-slate-700 text-slate-200">
                  <label className="cursor-pointer" data-testid="kcrypto-import-identity-label">
                    <Icons.Upload className="mr-2 h-4 w-4" /> Import identity
                    <input type="file" accept="application/json,.json" className="hidden" onChange={importFromFile} data-testid="kcrypto-import-identity-input" />
                  </label>
                </Button>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Engine Capabilities" icon="Cpu" className="mt-6" testId="kcrypto-capabilities-card">
        <ul className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          {[
            "SHA-256 hashing (WebCrypto crypto.subtle)",
            "CANONICAL JSON + RAW TEXT hash modes",
            "Deterministic canonicalization before hashing",
            "Timestamp generation (ISO 8601, local)",
            "Unique event / anchor IDs (crypto.getRandomValues)",
            "Chained records via previous_hash",
            "Verification by hash recalculation",
            "Tamper detection (HASH MISMATCH)",
            "Optional Ed25519 / ECDSA receipt signing",
            "Signature verification (VERIFIED / INVALID / UNSIGNED)",
            "Downloadable JSON evidence receipts + PDF packs",
            "Local verification of imported receipts",
          ].map((cap) => (
            <li key={cap} className="flex items-start gap-2 text-slate-300">
              <Icons.CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" />
              {cap}
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard title="Local Chain Hashes" icon="Link2" className="mt-6" testId="kcrypto-chain-card">
        {records.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500">No records in the chain yet.</p>
        ) : (
          <div className="space-y-2">
            {records.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 rounded border border-slate-800 bg-slate-950/50 p-2.5" data-testid={`chain-hash-${r.id}`}>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-slate-800 font-mono text-xs text-cyan-300">{i}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-slate-400">{r.payload_type} · {r.event_id} · {(r.hash_mode || "canonical") === "raw" ? "RAW" : "CANON"}</p>
                  <p className="truncate font-mono text-[11px] text-cyan-300">{r.sha256}</p>
                </div>
                <Icons.ArrowUp className="h-4 w-4 shrink-0 text-slate-600" aria-hidden="true" />
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
